import accounting from "accounting";
import moment from "moment";
import { parsePhoneNumber } from "libphonenumber-js";
import { CurrentUserType, UserParam } from "providers/types";

export const fullNumberToString = (val: number | undefined | null): string => {
  if (typeof val === "number" && !isNaN(val)) {
    return val.toFixed(2);
  } else {
    return "";
  }
};

export const stringToFullNumber = (val: string): number => {
  return Math.round(parseFloat(val));
};

//To get the decimal values
export const getDecimalNumber = (val: number): number => {
  let roundedNumber = Math.round(val * 100) / 100;

  return roundedNumber;
};

export const stringToDecimalNumber = (val: string): number => {
  let parsedNumber = parseFloat(val);
  let roundedNumber = Math.round(parsedNumber * 100) / 100;

  return roundedNumber;
};

export const stringToNumber = (val: string): number => {
  return val === "" ? 0 : parseFloat(val);
};

/* Comma seperated */
// export const stringToDollar = (val: string, currency: string = "$"): string => {  //Making the second argument optional(D)
//   return `${lendtechCurrency}${accounting.formatNumber(parseFloat(val), 2)}`;
// };

export const stringToDollar = (val: string, currency: string = "$") => {
  //Making the second argument optional(D)
  return `${currency}${accounting.formatNumber(parseFloat(val), 2)}`;
};

export const stringToDollarNoCents = (val: string): string => {
  return `$${accounting.formatNumber(parseFloat(val))}`;
};

export const numberToDollar = (val: number, currency: string = "$"): string => {
  return `${currency}${accounting.formatNumber(parseFloat(fullNumberToString(val)), 2)}`;
};

export const unformatDollarString = (val: string): number => {
  return accounting.unformat(val);
};

export const toggleAccountType = (type: string) => (type === "borrower" ? "lender" : "borrower");

export const borrowerOrUser = (type: string) => (type === "borrower" ? "user" : "borrower");

export const loanDetailsProgress = (loan: any) => {
  const invalid: string[] = [];
  const required: string[] = ["loanType", "asset", "name", "amount"];

  required.forEach(field => {
    if (!loan[field]) {
      invalid.push(field);
    }
  });

  return ((required.length - invalid.length) / required.length) * 100;
};

export const getCompanyCurrency = (user: any) => {
  let currency = "$";
  if (user.type === "lender") {
    currency = user.company.currency;
  } else if (user.type === "borrower") {
    currency = user.lender_company.currency;
  }
  return currency || "$";
};

export const shortLoanAmountNew = (
  val: string | number,
  {
    currency,
    stringForZero = "Not Provided",
  }: {
    currency?: string;
    stringForZero?: string;
  }
) => {
  return shortLoanAmount(val, currency, stringForZero);
};

export const shortLoanAmount = (
  val: string | number,
  currency?: string,
  stringForZero = "Not Provided"
) => {
  if (val === 0 || val === "0" || val === "" || isNaN(parseFloat(`${val}`))) {
    return stringForZero;
  }
  const fullInt = Math.round(parseFloat(`${val}`));
  return `${currency}${fullInt.toLocaleString()}`;
};

export const getUserTagline = (user: any) => {
  return user.type === "lender" ? user.job_title : (user.company || {}).name;
};

export const stringArrayToTags = (arr: any[]) => {
  return arr.map(item => ({
    label: item,
    value: item,
  }));
};

export const formatFieldTypeValue = (type: string, value: any, user?: UserParam | null): string => {
  try {
    const getCompanyConfigurations = (u: CurrentUserType | null | undefined) =>
      u?.company?.company_configurations[0];

    let userObj: CurrentUserType | null | undefined;
    if (user) {
      userObj = "user" in user ? user.user : user;
    } else {
      userObj = user;
    }

    const companyConfig = getCompanyConfigurations(userObj);

    const custom_funding_date = (companyConfig?.field_controls as any)?.funding_date;
    const custom_currency = (companyConfig?.custom_currency as any)?.currency;

    if (!value || value === Infinity) return "";

    const data = value;

    if (type === "percentage") {
      return `${parseFloat(data).toFixed(2)}%`;
    } else if (type === "dollar") {
      return stringToDollar(data, custom_currency);
    } else if (
      type === "textarea" ||
      type === "number" ||
      type === "text" ||
      type === "dropdown" ||
      type === "radio" ||
      type === "email" ||
      type === "boolean"
    ) {
      return String(data?.value ?? data);
    } else if (type === "encrypted") {
      return "********";
    } else if (type === "radioFloatingRate") {
      if (value && typeof value === "object") {
        const formattedData = Object.entries(value)
          .filter(([key]) => key === "LoanType" || key === "InterestRate")
          .map(([key, val]) => {
            if (key === "LoanType") {
              return `${val} :`;
            } else if (key === "InterestRate") {
              return `${parseFloat(val as string)}%`;
            } else {
              return `${key}: ${val}`;
            }
          })
          .join("\n");
        return formattedData;
      } else {
        return "";
      }
    } else if (type === "location") {
      return formatAddress(data);
    } else if (type === "date") {
      return custom_funding_date
        ? moment.utc(data).format("DD/MM/YYYY")
        : moment.utc(data).format("MM/DD/YYYY");
    } else if (type === "phone") {
      if (typeof value === "string" && value.startsWith("+")) {
        try {
          const phoneNumber = parsePhoneNumber(value);
          if (phoneNumber.country === "US") {
            return phoneNumber.formatNational();
          } else if (phoneNumber.country === "GB") {
            return phoneNumber.formatNational();
          } else {
            return phoneNumber.formatInternational();
          }
        } catch (error) {
          console.error("Error formatting phone number:", error);
          return value;
        }
      } else {
        return String(value);
      }
    }
    return JSON.stringify(value);
  } catch (e) {
    console.error(e);
  }
  return "Error in formatting";
};

export const getUniqueValuesByKey = (arrayOfObjects: any[] | undefined, key: string): string[] => {
  const uniqueValues = new Set<string>();

  if (!arrayOfObjects || !Array.isArray(arrayOfObjects)) {
    return [];
  }

  arrayOfObjects.forEach(obj => {
    if (obj && obj.hasOwnProperty && obj.hasOwnProperty(key) && typeof obj[key] === "string") {
      uniqueValues.add(obj[key]);
    }
  });

  return Array.from(uniqueValues);
};

export function formatAddress(address: any): string {
  if (typeof address === "string") return address;
  return address?.formatted || "Not provided";
}
